import { useContext } from 'react';
import { CompanyFormData, FormData, MSPFormData } from '../types';
import { getCookie } from '../util/cookies';
import { formatMarketingAttributionValueFromCookie } from '../util/formatMarketingAttributionValueFromCookie';
import { getAPIUrl } from '../util/getAPIUrl';
import { getExperimentVersion } from '../util/getExperimentVersion';
import { getGCLIDValue } from '../util/getGCLIDValue';
import optimizerABTestCookieValues from '../util/optimizerABTestCookieValues';
import { getTrialDates } from '../util/trialLength';
import { LP_PLUS_MARKETO, PRODUCT_SKUS } from '../consts';
import { useAppStore } from '../store/appStore';
import { httpClient } from './httpClient';

export const postSegmentService = async ({
  event,
  properties,
  signal,
}: {
  event: string;
  properties: {
    reason?: string;
    apiErrorDescription?: string;
    apiErrorCode?: string;
    sku?: string;
    journey_step?: string;
    source?: string;
    marketingOptOutShown?: boolean;
    contactPermission?: boolean;
  };
  signal?: AbortSignal;
}) => {
  try {
    await httpClient.post(
      `${getAPIUrl()}/lmiapi/segment/send-event?experiment_version=${getExperimentVersion()}`,
      {
        event,
        properties,
        platform: 'web',
      },
      { signal }
    );
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postBoomiService = async ({
  data,
  userKey,
  signal,
}: {
  data: FormData;
  userKey?: string;
  signal?: AbortSignal;
}) => {
  try {
    const { businessMax } = useAppStore.getState();

    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const dates = getTrialDates(data.trialLength);

    const params = new URLSearchParams(window.location.search);
    let isMax = params.has('max') || businessMax;

    const boomiData = {
      quantity: 1,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      country: getCookie('cciso') || '',
      optin: data.contactPermission,
      phone: data.phoneNumber,
      company: data.companyName,
      employees: data.companySize,
      language: data.language,
      title: data.jobTitle,
      ipAddress: data.clientIp,
      planType: data.flowType,
      trialStart: dates.trialStart,
      trialEnd: dates.trialEnd,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: isMax ? PRODUCT_SKUS.SKU_LPBUSINESS_SSO_MFA : data.trialSku,
      referringURL: window.location.href,
      programName: data.programName,
      programID: data.programID,
      webEnrichedFields: data.webEnrichedFields,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    await httpClient.post('/lpapi/form/SendTrialBoomi', boomiData, { signal });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postCompanyBoomiService = async ({
  data,
  userKey,
  signal,
}: {
  data: CompanyFormData;
  userKey: string;
  signal?: AbortSignal;
}) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const boomiData = {
      quantity: 1,
      country: getCookie('cciso') || '',
      ipAddress: data.clientIp,
      planType: data.flowType,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: data.trialSku,
      referringURL: window.location.href,
      programName: data.programName,
      programID: data.programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    await httpClient.post('/lpapi/form/SendTrialBoomi', boomiData, { signal });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postMSPBoomiService = async ({
  data,
  userKey,
  signal,
}: {
  data: MSPFormData;
  userKey: string;
  signal?: AbortSignal;
}) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();

    const dates = getTrialDates(data.trialLength);

    const boomiData = {
      quantity: 1,
      firstName: data.firstName,
      lastName: data.lastName,
      email: data.email,
      optin: data.contactPermission,
      phone: data.phoneNumber,
      company: data.companyName,
      employees: data.companySize,
      language: data.language,
      title: data.jobTitle,
      ipAddress: data.clientIp,
      planType: data.flowType,
      trialStart: dates.trialStart,
      trialEnd: dates.trialEnd,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      userKey: userKey,
      planKey: data.trialSku,
      referringURL: window.location.href,
      zipCode: data.zipCode,
      country: data.country,
      state: data.state,
      managedUsers: data.numberOfManagedUsers,
      programName: data.programName,
      programID: data.programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    await httpClient.post('/lpapi/form/SendMSPTrialBoomi', boomiData, { signal });
    return { success: true };
  } catch (error) {
    return { success: false };
  }
};

export const postTrialInterestBoomiService = async ({
  email,
  contactPermission,
  trialSku,
  programName,
  programID,
  businessMax,
  isLPPlus,
  signal,
}: {
  email: string;
  contactPermission: boolean;
  trialSku: string;
  programName?: string;
  programID?: string;
  businessMax?: boolean;
  isLPPlus?: boolean;
  signal?: AbortSignal;
}) => {
  try {
    const formattedMarketingAttributionObject = formatMarketingAttributionValueFromCookie();
    const abTestValues: string = optimizerABTestCookieValues();
    const params = new URLSearchParams(window.location.search);
    const isMax = params.has('max') || businessMax;
    const boomiData = {
      Email: email,
      country: getCookie('cciso') || '',
      optin: contactPermission,
      mkto_trk: window._mkto_trk ? window!._mkto_trk : '',
      chop_id: getCookie('chop_id') || '',
      gclid: getGCLIDValue(),
      promoCode: '',
      accountKey: '',
      universalID: '',
      productInterest: isMax
        ? PRODUCT_SKUS.SKU_LPBUSINESS_SSO_MFA
        : isLPPlus
          ? LP_PLUS_MARKETO.PRODUCT_INTEREST
          : trialSku,
      referringURL: window.location.href,
      programName: isLPPlus ? LP_PLUS_MARKETO.PROGRAM_NAME : programName,
      programID: isLPPlus ? LP_PLUS_MARKETO.PROGRAM_ID : programID,
      ...formattedMarketingAttributionObject,
      ...(abTestValues ? { experiment: optimizerABTestCookieValues() } : {}),
    };
    await httpClient.post('/lpapi/form/SendTrialInterest', boomiData, { signal });
    return { success: true };
  } catch (error) {
    console.warn('postTrialInterestBoomiService error:', error);
    return { success: false };
  }
};
